import React, { useState } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { addMarianaTekTag, removeMarianaTekTag } from '../services/api'

const Container = styled.div`
  background: ${theme.color.background.frame};
  padding: 0.65rem;
  border-radius: 5px;
  border: 1px solid ${theme.color.border.light};
  width: 260px;
  flex-shrink: 0;
`

const Title = styled.h3`
  font-size: 1.04rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const FormGroup = styled.div`
  margin-bottom: 0.52rem;
`

const Select = styled.select`
  width: 100%;
  padding: 0.52rem;
  border: 1px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 0.845rem;
  background: white;
  color: ${theme.color.text.base};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.52rem;
  margin-bottom: 0.65rem;
`

const Button = styled.button<{ variant?: 'add' | 'remove' }>`
  flex: 1;
  background: ${props => props.variant === 'remove' ? '#dc3545' : theme.color.accent.primary};
  color: white;
  border: none;
  padding: 0.52rem 0.78rem;
  border-radius: 4px;
  font-size: 0.845rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover:not(:disabled) {
    background: ${props => props.variant === 'remove' ? '#c82333' : '#b8002d'};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: ${theme.color.border.light};
    color: ${theme.color.text.grey};
    cursor: not-allowed;
    transform: none;
  }
`

const ActiveTagsSection = styled.div`
  margin-top: 0.65rem;
  padding-top: 0.65rem;
  border-top: 1px solid ${theme.color.border.light};
`

const ActiveTagsTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${theme.color.text.grey};
  margin-bottom: 0.52rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const ActiveTagsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.39rem;
  max-height: 200px;
  overflow-y: auto;
`

const ActiveTagItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.39rem;
  padding: 0.39rem;
  background: #e8f5e9;
  border: 1px solid #4caf50;
  border-radius: 4px;
  font-size: 0.78rem;
`

const TagIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`

const TagLabel = styled.span`
  flex: 1;
  color: ${theme.color.text.base};
`

const NoActiveTags = styled.div`
  font-size: 0.78rem;
  color: ${theme.color.text.grey};
  font-style: italic;
  padding: 0.52rem;
  text-align: center;
`

const ErrorMessage = styled.div`
  color: #d32f2f;
  background: #ffebee;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.78rem;
  margin-top: 0.52rem;
`

const SuccessMessage = styled.div`
  color: #2e7d32;
  background: #e8f5e9;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.78rem;
  margin-top: 0.52rem;
`

// Tag ID to icon mapping - same as UserProfile
const TAG_MAPPING: Record<number, { icon: string; description: string }> = {
  1: { icon: 'icon-employee.svg', description: 'Employee - Personal Account' },
  3: { icon: 'icon-high-maintenance.svg', description: 'Extra Care' },
  4: { icon: 'icon-medical.svg', description: 'Injury' },
  5: { icon: 'icon-military.svg', description: 'Military' },
  6: { icon: 'icon-student.svg', description: 'Student' },
  11: { icon: 'icon-corporate.svg', description: 'HQ Employee' },
  12: { icon: 'icon-originals.svg', description: 'Originals' },
  13: { icon: 'Mariana-Tag_RreU1d1.svg', description: 'Mariana Tek Employee' },
  14: { icon: 'Partner.svg', description: 'Partner' },
  15: { icon: 'Notes.svg', description: 'Notes' },
  16: { icon: 'first-responder2.svg', description: 'First Responder' },
  17: { icon: 'waiver_tag_vuilOfY.svg', description: 'Needs COVID-19 Waiver' },
  50: { icon: 'All_Star2x.svg', description: 'Silver' },
  51: { icon: 'cancellation.svg', description: 'Cancellation Forgiveness' },
  52: { icon: 'fuel_1_1.svg', description: 'Free Fuel Item' },
  53: { icon: 'Legend2x.svg', description: 'Black' },
  54: { icon: 'member.svg', description: 'Member' },
  55: { icon: 'Player2x.svg', description: 'Red' },
  56: { icon: 'LoyaltyVIP2x.svg', description: 'Platinum' },
  83: { icon: 'Tag_Soho_House_BVcPkjE.svg', description: 'Soho House' },
  84: { icon: 'tag_vaccinated_6iRe5PO.svg', description: 'Vaccinated' },
  149: { icon: '5_Off_Fuel_1.svg', description: '5% Fuel Item Discount' },
  150: { icon: '10_Off_Fuel_1_1_1.svg', description: '10% Fuel Item Discount' },
  151: { icon: 'Early_Booking.svg', description: 'Early Booking' },
  152: { icon: 'Priority_Waitlist.svg', description: 'Priority Waitlist' },
  182: { icon: 'barrys_corp_5.svg', description: '5% Corporate UK' },
  183: { icon: 'barrys_corp_10.svg', description: '10% Corporate UK' },
  184: { icon: 'barrys_corp_15.svg', description: '15% Corporate UK' },
  185: { icon: 'barrys_corp_20.svg', description: '20% Corporate UK' },
  215: { icon: 'exclusive_member_barrystag.svg', description: 'Exclusive Member' },
  248: { icon: 'barrus_u27.svg', description: 'Fuel Client Info' },
  281: { icon: 'Needs_Guardian_Tag2x.svg', description: 'Needs Guardian' },
  315: { icon: 'Barrys_hellweekfinisher.svg', description: 'Hell Week Finisher' },
  348: { icon: '6_Month_Member_Tag.svg', description: '6 Month Member' },
  349: { icon: 'first_timer.png', description: 'First Timer Referred Friend' }
}

interface UserTagManagementProps {
  marianaTekData: any
  onUpdate?: () => void
}

const UserTagManagement: React.FC<UserTagManagementProps> = ({ marianaTekData, onUpdate }) => {
  const [selectedTagId, setSelectedTagId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Extract user ID and current tags
  const marianaTekUserId = marianaTekData?.data?.[0]?.id
  const currentTags = marianaTekData?.data?.[0]?.relationships?.tags?.data || []
  const currentTagIds = currentTags.map((tag: any) => parseInt(tag.id))

  const handleAddTag = async () => {
    if (!selectedTagId || !marianaTekUserId) {
      setError('Please select a tag')
      setTimeout(() => setError(null), 3000)
      return
    }

    const tagId = parseInt(selectedTagId)

    if (currentTagIds.includes(tagId)) {
      setError('User already has this tag')
      setTimeout(() => setError(null), 3000)
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      await addMarianaTekTag(marianaTekUserId, tagId)
      setSuccess(`Added tag: ${TAG_MAPPING[tagId].description}`)
      setSelectedTagId('')

      setTimeout(() => setSuccess(null), 3000)

      if (onUpdate) {
        setTimeout(() => onUpdate(), 1000)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add tag')
      setTimeout(() => setError(null), 5000)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveTag = async () => {
    if (!selectedTagId || !marianaTekUserId) {
      setError('Please select a tag')
      setTimeout(() => setError(null), 3000)
      return
    }

    const tagId = parseInt(selectedTagId)

    if (!currentTagIds.includes(tagId)) {
      setError('User does not have this tag')
      setTimeout(() => setError(null), 3000)
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      await removeMarianaTekTag(marianaTekUserId, tagId)
      setSuccess(`Removed tag: ${TAG_MAPPING[tagId].description}`)
      setSelectedTagId('')

      setTimeout(() => setSuccess(null), 3000)

      if (onUpdate) {
        setTimeout(() => onUpdate(), 1000)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove tag')
      setTimeout(() => setError(null), 5000)
    } finally {
      setIsLoading(false)
    }
  }

  if (!marianaTekUserId) {
    return null
  }

  // Get active tags with their info
  const activeTags = currentTagIds
    .filter((tagId: number) => TAG_MAPPING[tagId])
    .map((tagId: number) => ({
      id: tagId,
      ...TAG_MAPPING[tagId]
    }))

  // Tags to exclude from the dropdown (tier tags and certain fuel/booking tags)
  const EXCLUDED_TAG_IDS = [50, 53, 54, 55, 149, 150, 151] // Silver, Black, Member, Red, 5% Fuel, 10% Fuel, Early Booking

  return (
    <Container>
      <Title>User Tag Management</Title>

      <FormGroup>
        <Select
          value={selectedTagId}
          onChange={(e) => setSelectedTagId(e.target.value)}
          disabled={isLoading}
        >
          <option value="">Select a tag...</option>
          {Object.entries(TAG_MAPPING)
            .filter(([tagIdStr]) => !EXCLUDED_TAG_IDS.includes(parseInt(tagIdStr)))
            .map(([tagIdStr, tagInfo]) => (
              <option key={tagIdStr} value={tagIdStr}>
                {tagInfo.description}
              </option>
            ))}
        </Select>
      </FormGroup>

      <ButtonGroup>
        <Button
          onClick={handleAddTag}
          disabled={isLoading || !selectedTagId}
        >
          {isLoading ? 'Working...' : 'Add Tag'}
        </Button>
        <Button
          variant="remove"
          onClick={handleRemoveTag}
          disabled={isLoading || !selectedTagId}
        >
          {isLoading ? 'Working...' : 'Remove Tag'}
        </Button>
      </ButtonGroup>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}
    </Container>
  )
}

export default UserTagManagement
